<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { lessonsApi, classesApi } from '@/api'
import { getColour } from '@/constants/colors'
import { nextOccurrenceOf, toLocalDateKey } from '@/utils/date'
import DatePicker from '@/components/inputs/DatePicker.vue'
import { AppButton } from '@/design-system/components'

const props = defineProps<{
  modelValue: boolean
  classId?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: []
}>()

interface LessonSlot {
  dayOfWeek: number
  startTime: string
  endTime: string
  room: string
  title: string
}

const weekDays = [
  { value: 1, short: 'Mon', name: 'Monday' },
  { value: 2, short: 'Tue', name: 'Tuesday' },
  { value: 3, short: 'Wed', name: 'Wednesday' },
  { value: 4, short: 'Thu', name: 'Thursday' },
  { value: 5, short: 'Fri', name: 'Friday' },
]

function getDefaultDates() {
  const nextMonday = nextOccurrenceOf('MONDAY')
  const endDate = new Date(nextMonday)
  endDate.setMonth(endDate.getMonth() + 4)
  return {
    startDate: toLocalDateKey(nextMonday),
    endDate: toLocalDateKey(endDate),
  }
}

const formClassId = ref('')
const form = ref(getDefaultDates())
const lessonSlots = ref<LessonSlot[]>([])
const showAddSlot = ref(false)
const newSlot = ref<LessonSlot>({
  dayOfWeek: 1,
  startTime: '09:00',
  endTime: '10:00',
  room: '',
  title: '',
})

const saving = ref(false)
const error = ref<string | null>(null)
const classes = ref<Array<{ id: string; name: string; colour?: string }>>([])
const loadingClasses = ref(false)

const effectiveClassId = computed(() => props.classId || formClassId.value)

const canSave = computed(() => effectiveClassId.value && form.value.startDate && form.value.endDate && lessonSlots.value.length > 0)
const canAddSlot = computed(() => newSlot.value.dayOfWeek && newSlot.value.startTime && newSlot.value.endTime)

const scheduleDuration = computed(() => {
  if (!form.value.startDate || !form.value.endDate) return null
  const start = new Date(form.value.startDate)
  const end = new Date(form.value.endDate)
  const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'End date must be after start date'
  const weeks = Math.floor(diffDays / 7)
  if (weeks === 0) return 'Less than 1 week'
  return `${weeks} week${weeks > 1 ? 's' : ''}`
})

async function loadClasses() {
  if (props.classId || classes.value.length > 0) return
  loadingClasses.value = true
  try {
    const res = await classesApi.classesGet()
    classes.value = (res.data as any[]).map((c: any) => ({ id: c.id, name: c.name, colour: c.colour }))
  } catch { /* ignore */ } finally {
    loadingClasses.value = false
  }
}

function getDayName(dayOfWeek: number): string {
  return weekDays.find(d => d.value === dayOfWeek)?.name || ''
}

function close() {
  emit('update:modelValue', false)
}

function resetForm() {
  formClassId.value = ''
  form.value = getDefaultDates()
  lessonSlots.value = []
  showAddSlot.value = false
  error.value = null
}

function addSlot() {
  if (!canAddSlot.value) return
  lessonSlots.value.push({ ...newSlot.value })
  showAddSlot.value = false
  newSlot.value = { dayOfWeek: 1, startTime: '09:00', endTime: '10:00', room: '', title: '' }
}

function removeSlot(index: number) {
  lessonSlots.value.splice(index, 1)
}

async function handleSubmit() {
  if (!canSave.value || saving.value) return
  saving.value = true
  error.value = null

  try {
    const dayNames = ['', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
    await lessonsApi.createLessonSchedule({
      classId: effectiveClassId.value,
      startDate: form.value.startDate,
      endDate: form.value.endDate,
      sessions: lessonSlots.value.map(slot => ({
        dayOfWeek: dayNames[slot.dayOfWeek] ?? 'MONDAY',
        startTime: slot.startTime,
        endTime: slot.endTime,
        room: slot.room || undefined,
        title: slot.title || undefined,
      })),
    })
    emit('created')
    close()
  } catch (err: unknown) {
    console.error('Failed to schedule lessons:', err)
    error.value = 'Failed to schedule lessons. Please try again.'
  } finally {
    saving.value = false
  }
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      resetForm()
      loadClasses()
    }
  }
)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] p-4">
        <div class="absolute inset-0 bg-black/30 backdrop-blur-[2px]" @click="close" />

        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="px-6 pt-6 pb-4 flex-shrink-0">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'calendar-week']" class="text-purple-500 text-sm" />
                </div>
                <div>
                  <h2 class="text-lg font-bold text-gray-900">Schedule Lessons</h2>
                  <p class="text-xs text-gray-400">Create a recurring weekly timetable</p>
                </div>
              </div>
              <button class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" @click="close">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 pb-2 space-y-5">
            <!-- Class selector (only when classId not provided) -->
            <div v-if="!classId">
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Class</label>
              <div v-if="loadingClasses" class="flex items-center gap-2 text-sm text-gray-400 py-2">
                <font-awesome-icon :icon="['fas', 'spinner']" class="animate-spin text-xs" />
                Loading...
              </div>
              <div v-else class="grid grid-cols-2 gap-1.5">
                <button
                  v-for="cls in classes"
                  :key="cls.id"
                  type="button"
                  class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all border"
                  :class="formClassId === cls.id
                    ? 'border-bc-indigo-300 bg-bc-indigo-50/60 shadow-sm'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'"
                  @click="formClassId = cls.id"
                >
                  <div class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: getColour(cls.colour).hex }" />
                  <span class="text-sm font-medium text-gray-800 truncate">{{ cls.name }}</span>
                </button>
              </div>
            </div>

            <!-- Period -->
            <div>
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Period</label>
              <div class="grid grid-cols-2 gap-3">
                <DatePicker v-model="form.startDate" placeholder="Start date" icon="calendar" />
                <DatePicker v-model="form.endDate" placeholder="End date" icon="flag" :min-date="form.startDate" />
              </div>
              <p v-if="scheduleDuration" class="text-xs text-gray-400 mt-1.5 flex items-center gap-1.5">
                <font-awesome-icon :icon="['fas', 'info-circle']" class="text-blue-400 text-[10px]" />
                {{ scheduleDuration }}
              </p>
            </div>

            <!-- Weekly Slots -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Weekly Slots</label>
                <button
                  type="button"
                  class="text-xs font-semibold text-bc-indigo-500 hover:text-bc-indigo-600 transition-colors"
                  @click="showAddSlot = true"
                >
                  + Add slot
                </button>
              </div>

              <!-- Empty state -->
              <div v-if="lessonSlots.length === 0 && !showAddSlot" class="text-center py-6 bg-gray-50/60 rounded-xl border border-dashed border-gray-200">
                <p class="text-sm text-gray-400 mb-1">No slots yet</p>
                <button type="button" class="text-xs font-semibold text-bc-indigo-500 hover:text-bc-indigo-600" @click="showAddSlot = true">
                  Add your first slot
                </button>
              </div>

              <!-- Slot list -->
              <div v-if="lessonSlots.length > 0" class="space-y-1.5" :class="{ 'mb-3': showAddSlot }">
                <div
                  v-for="(slot, index) in lessonSlots"
                  :key="index"
                  class="flex items-center gap-3 px-3.5 py-2.5 bg-gray-50 rounded-xl"
                >
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-gray-800">{{ getDayName(slot.dayOfWeek) }}</p>
                    <p class="text-xs text-gray-400">
                      {{ slot.startTime }} - {{ slot.endTime }}
                      <span v-if="slot.room" class="text-gray-300"> &middot; {{ slot.room }}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    class="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    @click="removeSlot(index)"
                  >
                    <font-awesome-icon :icon="['fas', 'xmark']" class="text-xs" />
                  </button>
                </div>
              </div>

              <!-- Inline add slot form -->
              <div v-if="showAddSlot" class="bg-gray-50 rounded-xl p-3.5 space-y-3 border border-gray-100">
                <!-- Day picker -->
                <div class="flex gap-1.5">
                  <button
                    v-for="day in weekDays"
                    :key="day.value"
                    type="button"
                    class="flex-1 py-2 text-xs font-semibold rounded-lg transition-colors"
                    :class="newSlot.dayOfWeek === day.value
                      ? 'bg-bc-indigo-500 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-bc-indigo-300'"
                    @click="newSlot.dayOfWeek = day.value"
                  >
                    {{ day.short }}
                  </button>
                </div>
                <!-- Time row -->
                <div class="flex items-center gap-2">
                  <input
                    v-model="newSlot.startTime"
                    type="time"
                    class="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400"
                  />
                  <span class="text-gray-300 text-sm">-</span>
                  <input
                    v-model="newSlot.endTime"
                    type="time"
                    class="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400"
                  />
                  <input
                    v-model="newSlot.room"
                    type="text"
                    placeholder="Room"
                    class="w-24 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400"
                  />
                </div>
                <!-- Actions -->
                <div class="flex justify-end gap-2">
                  <button type="button" class="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 rounded-lg transition-colors" @click="showAddSlot = false">
                    Cancel
                  </button>
                  <button
                    type="button"
                    :disabled="!canAddSlot"
                    class="px-3.5 py-1.5 bg-bc-indigo-500 text-white text-xs font-semibold rounded-lg hover:bg-bc-indigo-600 disabled:opacity-40 transition-colors"
                    @click="addSlot"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <!-- Error -->
            <div v-if="error" class="px-3.5 py-2.5 bg-red-50 rounded-xl text-red-600 text-sm">
              {{ error }}
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-end gap-2.5 px-6 py-4 flex-shrink-0">
            <AppButton variant="secondary" size="md" @click="close">
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              :disabled="!canSave"
              :loading="saving"
              @click="handleSubmit"
            >
              Schedule Lessons
            </AppButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
